/**
 * Page /admin/conservation — Conservation & purge RGPD des documents.
 *
 * Pilotée par le maire / DPO de la commune. Permet de :
 *   - activer/désactiver la purge automatique (OFF par défaut) ;
 *   - régler les durées de conservation (mois) par type de document ;
 *   - simuler (aperçu dry-run) ce qui serait purgé ;
 *   - lancer une purge manuelle (confirmation) ;
 *   - consulter le journal des purges.
 *
 * La purge automatique tourne par ailleurs via un worker cron hebdomadaire.
 * Ne concerne JAMAIS : urbanisme (archives publiques), comptable, concessions.
 */

import { useEffect, useState } from 'react';
import { Card, CardHeader, CardBody, Button, Input, Field, Badge, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { api, ApiException, type RgpdConfig, type RgpdPurgeResult, type RgpdPurgeLogEntry } from '@/lib/api';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { ShieldAlert, Loader2, Eye, Play, RotateCcw, CheckCircle2 } from 'lucide-react';

const REGLE_LABEL: Record<string, string> = {
  signalements_photos_contact: 'Signalements résolus (photos + contact)',
  pieces_scolaire: 'Pièces dossiers scolaires',
  pieces_association: 'Pièces dossiers associations',
  pieces_dechetterie: 'Pièces dossiers déchetterie',
  comptes_inactifs: 'Comptes administrés inactifs',
};

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function ConservationRgpdPage() {
  const { confirm, dialogElement } = useConfirmDialog();
  const [cfg, setCfg] = useState<RgpdConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [preview, setPreview] = useState<RgpdPurgeResult[] | null>(null);
  const [busy, setBusy] = useState<'preview' | 'run' | null>(null);
  const [journal, setJournal] = useState<RgpdPurgeLogEntry[]>([]);

  async function load() {
    setLoading(true);
    try {
      const [c, j] = await Promise.all([api.rgpd.getConfig(), api.rgpd.journal()]);
      setCfg(c);
      setJournal(j.entries);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function upd<K extends keyof RgpdConfig>(k: K, v: RgpdConfig[K]) {
    setCfg((c) => (c ? { ...c, [k]: v } : c));
  }
  function moisInput(k: keyof RgpdConfig, label: string) {
    if (!cfg) return null;
    return (
      <Field label={label} htmlFor={`m-${k}`}>
        <Input id={`m-${k}`} type="number" min={0} max={240} value={Number(cfg[k])}
          onChange={(e) => upd(k, Math.max(0, Number(e.target.value) || 0) as never)} />
      </Field>
    );
  }

  async function save() {
    if (!cfg) return;
    setSaving(true); setError(null); setOk(null);
    try {
      const saved = await api.rgpd.putConfig(cfg);
      setCfg(saved);
      setOk('Réglages enregistrés.');
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  }

  async function doPreview() {
    setBusy('preview'); setError(null); setOk(null); setPreview(null);
    try {
      const r = await api.rgpd.preview();
      setPreview(r.results);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Aperçu impossible.');
    } finally {
      setBusy(null);
    }
  }

  async function doRun() {
    const confirmed = await confirm({
      title: 'Lancer la purge maintenant ?',
      description: 'Les fichiers concernés seront envoyés à la corbeille (récupérables ~7 jours), et les métadonnées conservées. Cette action est enregistrée au journal.',
      confirmLabel: 'Lancer la purge',
      danger: true,
    });
    if (!confirmed) return;
    setBusy('run'); setError(null); setOk(null);
    try {
      const r = await api.rgpd.run();
      if (r.skipped) {
        setOk('La purge est désactivée (interrupteur sur OFF) — rien n\'a été fait.');
      } else {
        const total = r.results.reduce((n, x) => n + x.objetsPurges, 0);
        setOk(`Purge effectuée : ${total} fichier(s) envoyé(s) à la corbeille.`);
      }
      await load();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Purge impossible.');
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted" /></div>;
  }
  if (!cfg) {
    return <Alert variant="error">{error ?? 'Configuration indisponible.'}</Alert>;
  }

  return (
    <div className="space-y-6">
      {dialogElement}
      <div className="flex items-center gap-3">
        <LogoMark icon={ShieldAlert} accent="tech" size="md" />
        <div>
          <SectionEyebrow>RGPD</SectionEyebrow>
          <h1 className="text-xl font-semibold text-ink">Conservation des données</h1>
        </div>
      </div>

      <Alert variant="info">
        Cette page pilote la purge des <strong>fichiers</strong> (justificatifs scannés, photos) au terme
        de leur durée de conservation, tout en gardant la <strong>trace</strong> des décisions.
        L'urbanisme (archives publiques), le comptable (10 ans) et les concessions ne sont jamais purgés.
        La suppression passe par une corbeille (récupérable ~7 jours).
      </Alert>

      {error && <Alert variant="error">{error}</Alert>}
      {ok && <Alert variant="success" icon={CheckCircle2}>{ok}</Alert>}

      {/* Interrupteur global + durées */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-ink">Réglages de conservation</h2>
            <Badge variant={cfg.purgeActive ? 'success' : 'neutral'}>
              {cfg.purgeActive ? 'Purge active' : 'Purge désactivée'}
            </Badge>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={cfg.purgeActive}
              onChange={(e) => upd('purgeActive', e.target.checked)}
              className="mt-1 h-4 w-4" />
            <span className="text-sm text-ink">
              <strong>Activer la purge automatique</strong>
              <span className="block text-muted">Désactivé par défaut. À activer après validation par votre DPO. Le worker hebdomadaire ne traitera votre commune que si cette case est cochée.</span>
            </span>
          </label>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {moisInput('signalementsMois', 'Signalements résolus (mois)')}
            {moisInput('piecesScolaireMois', 'Pièces scolaires (mois)')}
            {moisInput('piecesAssoMois', 'Pièces associations (mois)')}
            {moisInput('piecesDechetterieMois', 'Pièces déchetterie (mois)')}
          </div>

          <div className="border-t border-border pt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={cfg.notesFraisPiecesActive}
                onChange={(e) => upd('notesFraisPiecesActive', e.target.checked)}
                className="mt-1 h-4 w-4" />
              <span className="text-sm text-ink">
                <strong>Purger les justificatifs de notes de frais</strong>
                <span className="block text-muted">Pièces comptables (tickets, factures) des notes remboursées ou refusées. Conservation légale conseillée : 10 ans (120 mois). Les notes elles-mêmes restent dans le journal ; seuls les fichiers sont retirés.</span>
              </span>
            </label>
            <div className="mt-3 max-w-xs">
              {moisInput('notesFraisPiecesMois', 'Conservation des justificatifs (mois)')}
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={cfg.justificatifsRhActive}
                onChange={(e) => upd('justificatifsRhActive', e.target.checked)}
                className="mt-1 h-4 w-4" />
              <span className="text-sm text-ink">
                <strong>Purger les justificatifs RH</strong>
                <span className="block text-muted">Arrêts de travail et pièces jointes de demandes d'absence. Conservation conseillée : 5 ans (60 mois). Les absences restent enregistrées ; seuls les fichiers sont retirés.</span>
              </span>
            </label>
            <div className="mt-3 max-w-xs">
              {moisInput('justificatifsRhMois', 'Conservation des justificatifs RH (mois)')}
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={cfg.comptesInactifsActive}
                onChange={(e) => upd('comptesInactifsActive', e.target.checked)}
                className="mt-1 h-4 w-4" />
              <span className="text-sm text-ink">
                <strong>Anonymiser les comptes administrés inactifs</strong>
                <span className="block text-muted">Uniquement les comptes citoyens (jamais le personnel ni les élus).</span>
              </span>
            </label>
            <div className="mt-3 max-w-xs">
              {moisInput('comptesInactifsMois', 'Inactivité avant anonymisation (mois)')}
            </div>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <Button onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
            </Button>
            <Button variant="secondary" onClick={doPreview} disabled={busy !== null}>
              {busy === 'preview' ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Eye className="h-4 w-4" /> Aperçu (simulation)</>}
            </Button>
            <Button variant="secondary" onClick={doRun} disabled={busy !== null}>
              {busy === 'run' ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="h-4 w-4" /> Lancer la purge</>}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Aperçu dry-run */}
      {preview && (
        <Card>
          <CardHeader><h2 className="font-semibold text-ink">Aperçu — ce qui serait purgé maintenant</h2></CardHeader>
          <CardBody>
            {preview.length === 0 ? (
              <p className="text-sm text-muted">Rien à purger (la purge est peut-être désactivée).</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-muted border-b border-border">
                  <th className="py-2">Règle</th><th className="py-2 text-right">Fichiers</th><th className="py-2 text-right">Lignes</th>
                </tr></thead>
                <tbody>
                  {preview.map((r) => (
                    <tr key={r.regle} className="border-b border-border/50">
                      <td className="py-2">{REGLE_LABEL[r.regle] ?? r.regle}</td>
                      <td className="py-2 text-right">{r.objetsPurges}</td>
                      <td className="py-2 text-right">{r.lignesTraitees}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>
      )}

      {/* Journal */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4 text-muted" />
            <h2 className="font-semibold text-ink">Journal des purges</h2>
          </div>
        </CardHeader>
        <CardBody>
          {journal.length === 0 ? (
            <p className="text-sm text-muted">Aucune purge enregistrée pour le moment.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-muted border-b border-border">
                <th className="py-2">Date</th><th className="py-2">Règle</th><th className="py-2 text-right">Fichiers</th><th className="py-2 text-right">Lignes</th>
              </tr></thead>
              <tbody>
                {journal.map((e) => (
                  <tr key={e.id} className="border-b border-border/50">
                    <td className="py-2 whitespace-nowrap">{fmtDate(e.created_at)}</td>
                    <td className="py-2">{REGLE_LABEL[e.regle] ?? e.regle}</td>
                    <td className="py-2 text-right">{e.objets_purges}</td>
                    <td className="py-2 text-right">{e.lignes_traitees}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
